<script setup lang="ts">
import { ref, defineProps, watch, Ref, computed } from "vue";
import { useVaccinationForm } from "@/composables/tasks/vaccination/useVaccinationForm";
import { useRecord } from "@/composables/tasks/vaccination/useVaccinationRecords";
import { useEquipment } from "@/composables/tasks/useEquipment";
import { useVaccination } from "@/composables/tasks/vaccination/useVaccination";
import { useWeight } from "@/composables/tasks/vaccination/useVaccinationWeight";
import { VaccinationProgressEntry, VaccineConfiguration } from "@/types/v2";
import { useVaccinationConfigurations } from "@/composables/tasks/vaccination/useVaccinationConfigurations";
import { useVaccinationTaskOperations } from "@/composables/tasks/vaccination/useVaccinationOperations";
import { useVaccinationProgress } from "@/composables/tasks/vaccination/useVaccinationProgress";

import ModalBox from "@/components/v2/Modal/ModalBox.vue";

// ===================================================================
// Props
// ===================================================================
interface Props {
    configuration: VaccineConfiguration;
    show: boolean;
    onClose?: () => void;
}
const props = defineProps<Props>();

// ===================================================================
// STATE - Modal
// ===================================================================
const showModal: Ref<boolean> = ref(props.show);

// ===================================================================
// COMPOSABLES
// ===================================================================
const {
    vaccinated,
    weight,
    comment,
    successMessage,

    isWeightDisabled,
    isVaccinatedDisabled,
    canSave,

    incrementVaccinated,
    decrementVaccinated,
    resetForm
} = useVaccinationForm();

const { createVaccinationRecord, createWeightRecord } = useRecord();
const { getEquipmentConfigurations } = useEquipment();
const { isIndividualWeightType, isRandomWeightType } = useWeight();

// ===================================================================
// Administration Equipment
// ===================================================================
// Get administration equipment configuration
const administrationEquipment = computed(() => {
    return getEquipmentConfigurations.value.find(config =>
        config.equipmentType.name.toLowerCase() === "administration equipment"
    );
});

// Get administration equipment options
const administrationEquipmentOptions = computed(() => {
    if (administrationEquipment.value?.equipmentType?.configOptions) {
        const typeOption = administrationEquipment.value.equipmentType.configOptions.find(opt => opt.name === "type");
        return typeOption?.options || [];
    }
    return [];
});

// Update the admin equipment type
const updateAdminEquipmentType = (type: string) => {
    if (administrationEquipment.value) {
        administrationEquipment.value.values.type = type;
        administrationEquipment.value.updated_at = new Date().toISOString();
    }
};

// ===================================================================
// ACTIONS - Modal
// ===================================================================
const closeModal = () => {
    if (props.onClose) {
        props.onClose();
    }
};

// ===================================================================
// ACTIONS - Save Vaccination Record
// ===================================================================
const handleSaveVaccinationData = (keepModalOpen = false) => {
    if (!canSave.value || !props.configuration) return;

    // Create the vaccination record (existing functionality)
    createVaccinationRecord(props.configuration.key, {
        vaccinated: vaccinated.value,
        weight: weight.value,
        comment: comment.value,
        // Include the administration equipment type
        administrationEquipment: administrationEquipment.value?.values?.type || null
    } as VaccinationProgressEntry);

    // Create a separate weight record if weight is provided (new functionality)
    if (weight.value > 0) {
        const tank = props.configuration.destinationTank;
        const group = props.configuration.destinationGroup;
        
        createWeightRecord({
            weightId: null,
            configurationKey: props.configuration.key,
            weight: weight.value,
            comment: comment.value || `Weight recorded during IVP administration`,
            timestamp: new Date().toISOString(),
            tank: tank ? {
                ...tank,
                // Ensure both name and label are available for display compatibility
                label: tank.label || tank.name,
                name: tank.name || tank.label
            } : null,
            group: group || null
        });
    }

    resetForm();

    if (!keepModalOpen) {
        return closeModal();
    }
};

// ===================================================================
// Watchers
// ===================================================================
watch(
    () => props.show,
    (newVal) => {
        showModal.value = newVal;
    }
);
</script>

<template>
    <ModalBox v-if="showModal" @close="closeModal">
        <template #header>
            <h1 class="text-xl font-semibold">Record IVP Administration</h1>
        </template>

        <template #main>
            <!-- Administration Equipment Selection -->
            <div v-if="administrationEquipmentOptions.length > 0 && administrationEquipment" class="mb-4">
                <label class="block text-gray-700 mb-2">Administration Equipment:</label>
                <select
                    v-model="administrationEquipment.values.type"
                    @change="updateAdminEquipmentType(administrationEquipment.values.type)"
                    class="form-select bg-white p-2.5 block w-full border border-gray-300 rounded shadow-sm"
                >
                    <option v-for="option in administrationEquipmentOptions" :key="option" :value="option">
                        {{ option }}
                    </option>
                </select>
            </div>

            <!-- Vaccination input -->
            <div class="mb-4">
                <label class="block text-gray-700 mb-2">Administer IVP:</label>
                <div class="flex items-stretch gap-2">
                    <input type="number" v-model.number="vaccinated" :disabled="isVaccinatedDisabled" min="1" pattern="\d*"
                           class="form-input p-2 block flex-1 border border-gray-300 rounded shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:cursor-not-allowed" />
                    <div class="flex flex-row gap-1">
                        <button @click="incrementVaccinated" :disabled="isVaccinatedDisabled"
                                class="p-1 w-10 text-white bg-primary rounded hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-bold">
                            +
                        </button>
                        <button @click="decrementVaccinated" :disabled="isVaccinatedDisabled || vaccinated <= 1"
                                class="p-1 w-10 text-white bg-primary rounded hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-lg font-bold">
                            −
                        </button>
                    </div>
                </div>
            </div>

            <div class="mb-4">
                <!-- Weight input -->
                <label class="block text-gray-700 mb-2">Weight:</label>
                <input type="number" v-model="weight" :disabled="isWeightDisabled === true" min="0"
                       class="form-input p-2 block w-full border border-gray-300 rounded shadow-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed" />
            </div>

            <!-- Comment input -->
            <div class="mb-4">
                <label class="block text-gray-700 mb-2">Comment:</label>
                <textarea v-model="comment" placeholder="Leave a comment"
                          class="form-input p-2 block w-full border border-gray-300 rounded shadow-sm focus:ring-indigo-500 focus:border-indigo-500 min-h-[100px]"></textarea>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-between w-full">
                <div class="flex items-center gap-2">
                    <button
                        @click="handleSaveVaccinationData(false)" type="button" :disabled="!canSave"
                        class="text-white bg-primary hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Save and close
                    </button>
                    <button
                        @click="handleSaveVaccinationData(true)" type="button" :disabled="!canSave"
                        class="text-white bg-green-600 hover:bg-green-500 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-50 disabled:cursor-not-allowed">
                        {{ successMessage !== "" ? "Save " + successMessage : "Save" }}
                    </button>
                </div>
                <button
                    @click="closeModal" type="button"
                    class="text-gray-500 bg-white hover:bg-gray-100 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg border border-gray-200 text-sm font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10"
                >
                    Back
                </button>
            </div>
        </template>
    </ModalBox>
</template>
